:host {
  @apply cursor-pointer flex relative text-color-3;
}

@include disabled();

@mixin range-part-base() {
  &:before,
  &:after {
    @apply absolute pointer-events-none;
    inset-block: 0;
    content: "";
    block-size: var(--calcite-internal-day-size);
    inline-size: var(--calcite-internal-day-size);
  }
}

@mixin range-part-edge-end() {
  &:before {
    inset-inline-end: 50%;
  }
  &:after {
    inset-inline-start: 50%;
    border-start-end-radius: var(--calcite-internal-day-size);
    border-end-end-radius: var(--calcite-internal-day-size);
    inline-size: calc(var(--calcite-internal-day-size) / 2);
  }
}

@mixin range-part-edge-start() {
  &:before {
    inset-inline-end: 50%;
    border-start-start-radius: var(--calcite-internal-day-size);
    border-end-start-radius: var(--calcite-internal-day-size);
    inline-size: calc(var(--calcite-internal-day-size) / 2);
  }
  &:after {
    inset-inline-start: 50%;
  }
}

@mixin range-part-middle() {
  &:before {
    inset-inline-end: 50%;
    border-radius: 0;
  }
  &:after {
    inset-inline-start: 50%;
    border-radius: 0;
  }
}

.day-v-wrapper {
  @apply flex-auto;
}

.day-wrapper {
  @apply flex
    flex-col
    items-center
    relative;
}

:host([range]),
:host([range-hover]) {
  .day-wrapper {
    @include range-part-base();
  }
}

.day {
  @apply text-n2h
    text-color-3
    flex
    focus-base
    items-center
    justify-center
    rounded-full
    leading-none
    transition-default
    z-default;
  background: none;
  box-shadow: 0 0 0 2px transparent;
  block-size: var(--calcite-internal-day-size);
  inline-size: var(--calcite-internal-day-size);
}

.text {
  @apply mt-px mb-0;
  margin-inline-start: theme("spacing.0");
}

:host([scale="s"]) {
  --calcite-internal-day-size: 27px;

  .day-v-wrapper {
    @apply py-0.5;
  }
  .day-wrapper {
    @apply p-0;
  }
  .day {
    @apply text-n2;
  }
}

:host([scale="m"]) {
  --calcite-internal-day-size: 33px;

  .day-v-wrapper {
    @apply py-1;
  }
  .day-wrapper {
    @apply p-0;
  }
  .day {
    @apply text-n1;
  }
}

:host([scale="l"]) {
  --calcite-internal-day-size: 43px;

  .day-v-wrapper {
    @apply py-1;
  }
  .day-wrapper {
    @apply px-1;
  }
  .day {
    @apply text-0;
  }
}

:host(:not([current-month])) .day {
  @apply opacity-disabled;
}

:host(:hover:not([disabled]):not([selected])),
:host([active]:not([range]):not([selected])) {
  & .day {
    @apply bg-foreground-2 text-color-1;
  }
}

:host(:focus),
:host([active]) {
  @apply outline-none;
}

:host(:focus:not([disabled])) .day {
  @apply focus-outset;
}

:host([selected]) .day {
  @apply font-medium;
  background-color: var(--calcite-color-brand);
  color: var(--calcite-color-foreground-1);
}

:host(:focus:not([disabled])),
:host([start-of-range]:not(:focus)),
:host([end-of-range]:not(:focus)) {
  .day {
    box-shadow: 0 0 0 2px var(--calcite-color-foreground-1);
  }
}

:host([range-hover]:not([selected])),
:host([highlighted]:not([selected])) {
  .day-wrapper {
    @include range-part-middle();
  }

  .day {
    @apply text-color-1;
  }
}

:host([highlighted]),
:host([selected]:not(.hover--outside-range)) {
  .day-wrapper {
    &:before,
    &:after {
      background-color: var(--calcite-color-foreground-current);
    }
  }
}

:host([range-hover]:not([selected])) {
  .day-wrapper {
    &:before,
    &:after {
      @apply bg-foreground-2;
    }
  }
}

:host(:hover[range-hover]:not([selected]).focused--end),
:host([highlighted][end-of-range]),
:host([highlighted][range-edge="end"]),
:host([range-hover][range-edge="end"]),
:host(:hover[range-hover].focused--end.hover--outside-range) {
  .day-wrapper {
    @include range-part-edge-end();
  }
}

:host([highlighted][start-of-range]),
:host([highlighted][range-edge="start"]),
:host([range-hover][range-edge="start"]),
:host(:hover[range-hover]:not([selected]).focused--start),
:host([start-of-range].hover--inside-range),
:host(:hover[range-hover].focused--start.hover--outside-range) {
  .day-wrapper {
    @include range-part-edge-start();
  }
}

:host([range-hover][start-of-range][range-edge="end"]),
:host([range-hover][end-of-range][range-edge="start"]),
:host([start-of-range][range-edge="end"].hover--inside-range),
:host([end-of-range]) {
  .day-wrapper {
    &:after,
    &:before {
      content: unset;
    }
  }
}

:host(:hover[range-hover]:not([selected]).focused--start),
:host(:hover[range-hover]:not([selected]).focused--end),
:host(:hover[range-hover]:not([selected]).focused--start.hover--outside-range),
:host(:hover[range-hover]:not([selected]).focused--end.hover--outside-range) {
  .day {
    box-shadow: 0 0 0 2px var(--calcite-color-foreground-1);
  }
}

@media (forced-colors: active) {
  .day {
    @apply rounded-none;
  }

  :host([selected]) {
    outline: 2px solid canvasText;
  }

  :host(:hover:not([selected])) {
    .day {
      border-radius: 50%;
    }
  }

  :host([range][selected]),
  :host([highlighted]),
  :host([range-hover]:not([selected])) {
    .day-wrapper {
      &:before,
      &:after {
        background-color: highlight;
      }
    }
  }

  :host([range-hover]),
  :host([range][selected][start-of-range]),
  :host([range][selected][end-of-range]) {
    .day-wrapper {
      &:before,
      &:after {
        background-color: canvas;
      }
    }
  }
}

@include base-component();
